/**
 * See it in action — autoplaying hosted-video gallery
 * -----------------------------------------------------------------------------
 * <sia-gallery> lazily loads each hosted video, autoplays it muted while it is
 * in the viewport, pauses it when it scrolls away, lets a shopper unmute a
 * single video at a time, and (on desktop) opens one full size in a dialog.
 *
 * Works as a standalone section and as a block inside "Product information".
 */
if (!customElements.get('sia-gallery')) {
  const DESKTOP = '(min-width: 750px)';

  class SiaGallery extends HTMLElement {
    connectedCallback() {
      this.autoplay = this.dataset.autoplay !== 'false';
      this.zoomEnabled = this.dataset.lightbox !== 'false';
      this.cards = Array.from(this.querySelectorAll('.sia-card'));
      this.videos = this.cards
        .map((card) => card.querySelector('video'))
        .filter(Boolean);

      if (!this.videos.length) return;

      this.lightbox = this.querySelector('.sia-lightbox');
      this.stage = this.querySelector('.sia-lightbox__stage');
      this.zoomLabel =
        (this.lightbox && this.lightbox.getAttribute('aria-label')) ||
        'Watch full size';
      this.visible = new Set();
      this.reduceMotion = window.matchMedia(
        '(prefers-reduced-motion: reduce)'
      ).matches;

      this.videos.forEach((video) => {
        // Required for inline autoplay on iOS.
        video.muted = true;
        video.setAttribute('muted', '');
        video.playsInline = true;
        video.setAttribute('playsinline', '');

        video.addEventListener('play', () => this.setCardState(video, false));
        video.addEventListener('pause', () => this.setCardState(video, true));
        video.addEventListener('timeupdate', () => this.updateProgress(video));
      });

      // Lightbox first: it decides whether zoom is really available, which
      // bindControls needs when it wires up the tiles.
      this.bindLightbox();
      this.bindControls();
      this.observe();
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    /* -------------------------------------------------------------- state - */

    setCardState(video, paused) {
      const card = video.closest('.sia-card');
      if (card) card.classList.toggle('is-paused', paused);
    }

    // Fill the thin progress bar under/over each video as it plays.
    updateProgress(video) {
      const card = video.closest('.sia-card');
      const fill = card && card.querySelector('.sia-card__progress__fill');
      if (!fill) return;

      const duration = video.duration;
      if (!duration || Number.isNaN(duration)) {
        fill.style.width = '0';
        return;
      }

      const pct = Math.min(Math.max((video.currentTime / duration) * 100, 0), 100);
      const prev = parseFloat(fill.style.width) || 0;

      // The video looped back to ~0. Snap the bar to 0 instead of animating
      // backwards through the whole track.
      if (pct < prev && pct < 1) {
        fill.style.transition = 'none';
        fill.style.width = '0';
        void fill.getBoundingClientRect();
        fill.style.transition = '';
        return;
      }

      fill.style.width = `${pct}%`;
    }

    get canZoom() {
      return (
        this.zoomEnabled && this.lightbox && window.matchMedia(DESKTOP).matches
      );
    }

    /* ------------------------------------------------------------ playback - */

    play(video) {
      // Bumping preload is enough to start the download — calling load() here
      // would abort the play() that follows it.
      if (video.preload === 'none') video.preload = 'auto';

      const promise = video.play();
      if (!promise || typeof promise.catch !== 'function') return;

      promise.catch(() => {
        // Refused (low power mode, data saver) or aborted because the file was
        // still loading. Show the play button, and retry once when it's ready.
        this.setCardState(video, true);

        if (video.dataset.playRetried) return;
        video.dataset.playRetried = 'true';

        video.addEventListener(
          'canplay',
          () => {
            if (!this.canAutoplay(video)) return;
            const retry = video.play();
            if (retry && typeof retry.catch === 'function') {
              retry.catch(() => this.setCardState(video, true));
            }
          },
          { once: true }
        );
      });
    }

    pause(video) {
      if (!video.paused) video.pause();
    }

    /* ------------------------------------------------------------ viewport - */

    observe() {
      if (!('IntersectionObserver' in window)) {
        if (this.autoplay && !this.reduceMotion) {
          this.videos.forEach((video) => this.play(video));
        }
        return;
      }

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target;

            if (entry.isIntersecting) {
              this.visible.add(video);
              if (this.canAutoplay(video)) this.play(video);
            } else {
              this.visible.delete(video);
              this.pause(video);
              // A video that scrolls out of view gives its sound back.
              if (!video.muted) this.setMuted(video, true);
            }
          });
        },
        // Overlapping the middle 70% of the screen counts as "on screen". An
        // area threshold would misfire on 9:16 cards taller than the viewport.
        { threshold: 0, rootMargin: '-15% 0px -15% 0px' }
      );

      this.videos.forEach((video) => {
        this.setCardState(video, true);
        this.observer.observe(video);
      });
    }

    canAutoplay(video) {
      return (
        this.autoplay &&
        !this.reduceMotion &&
        !video.dataset.userPaused &&
        this.isOnScreen(video) &&
        !(this.lightbox && this.lightbox.open)
      );
    }

    isOnScreen(video) {
      return this.observer ? this.visible.has(video) : true;
    }

    /* ------------------------------------------------------------ controls - */

    bindControls() {
      this.cards.forEach((card) => {
        const video = card.querySelector('video');
        if (!video) return;

        const media = card.querySelector('.sia-card__media');
        const sound = card.querySelector('.sia-card__sound');

        if (media) {
          media.addEventListener('click', (event) => {
            if (event.target.closest('.sia-card__controls')) return;
            this.activate(video);
          });

          // There is no visible enlarge button, so the tile itself carries the
          // keyboard affordance. Enter on the sound button is filtered out.
          if (this.zoomEnabled) {
            media.tabIndex = 0;
            media.setAttribute('role', 'button');
            media.setAttribute('aria-label', this.zoomLabel);
            media.addEventListener('keydown', (event) => {
              if (event.target !== media) return;
              if (event.key !== 'Enter' && event.key !== ' ') return;
              event.preventDefault();
              this.activate(video);
            });
          }
        }

        if (sound) {
          sound.addEventListener('click', (event) => {
            event.preventDefault();
            event.stopPropagation();

            const unmute = video.muted;
            if (unmute) {
              // Only one video plays out loud at a time, page-wide.
              document
                .querySelectorAll('sia-gallery video')
                .forEach((other) => {
                  if (other !== video) this.setMuted(other, true);
                });
            }

            this.setMuted(video, !unmute);
            if (unmute && video.paused) this.play(video);
          });
        }
      });
    }

    // Desktop opens the big view; mobile plays/pauses in place.
    activate(video) {
      if (this.canZoom) {
        this.openLightbox(video);
        return;
      }

      if (video.paused) {
        delete video.dataset.userPaused;
        this.play(video);
      } else {
        video.dataset.userPaused = 'true';
        this.pause(video);
      }
    }

    setMuted(video, muted) {
      video.muted = muted;
      const card = video.closest('.sia-card');
      const button = card && card.querySelector('.sia-card__sound');
      if (button) button.setAttribute('aria-pressed', muted ? 'false' : 'true');
    }

    /* ----------------------------------------------------------- lightbox - */

    bindLightbox() {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') {
        this.zoomEnabled = false;
        return;
      }

      const close = this.lightbox.querySelector('.sia-lightbox__close');
      if (close) close.addEventListener('click', () => this.closeLightbox());

      // Clicking the backdrop — anything outside the video — closes it.
      this.lightbox.addEventListener('click', (event) => {
        if (event.target === this.lightbox) this.closeLightbox();
      });

      // Esc closes the dialog itself. Listen for both signals and keep the
      // teardown idempotent, rather than trusting one event to arrive.
      this.lightbox.addEventListener('cancel', () => this.closeLightbox());
      this.lightbox.addEventListener('close', () => this.cleanUpLightbox());
    }

    closeLightbox() {
      if (this.lightbox.open) this.lightbox.close();
      this.cleanUpLightbox();
    }

    cleanUpLightbox() {
      if (!this.stage.firstChild) return;

      this.stage.replaceChildren();
      document.documentElement.style.overflow = '';

      // Resume whatever is still on screen behind the dialog.
      this.visible.forEach((video) => {
        if (this.canAutoplay(video)) this.play(video);
      });
    }

    openLightbox(video) {
      if (!this.lightbox || typeof this.lightbox.showModal !== 'function') return;

      const large = document.createElement('video');
      large.controls = true;
      large.loop = true;
      large.autoplay = true;
      large.playsInline = true;
      large.setAttribute('playsinline', '');
      large.preload = 'auto';
      if (video.poster) large.poster = video.poster;

      const sources = video.querySelectorAll('source');
      if (sources.length) {
        sources.forEach((source) => {
          const copy = document.createElement('source');
          copy.src = source.src;
          if (source.type) copy.type = source.type;
          large.appendChild(copy);
        });
      } else if (video.currentSrc || video.src) {
        large.src = video.currentSrc || video.src;
      }

      // Pick up where the tile left off.
      const startAt = video.currentTime;
      large.addEventListener(
        'loadedmetadata',
        () => {
          if (startAt && startAt < large.duration) large.currentTime = startAt;
        },
        { once: true }
      );

      this.videos.forEach((item) => {
        this.pause(item);
        if (!item.muted) this.setMuted(item, true);
      });

      this.stage.replaceChildren(large);
      document.documentElement.style.overflow = 'hidden';
      this.lightbox.showModal();

      const close = this.lightbox.querySelector('.sia-lightbox__close');
      if (close) close.focus();

      // The click is a user gesture, so sound is allowed here.
      large.muted = false;
      const promise = large.play();
      if (promise && typeof promise.catch === 'function') {
        promise.catch(() => {
          large.muted = true;
          large.play().catch(() => {});
        });
      }
    }
  }

  customElements.define('sia-gallery', SiaGallery);
}
